import { Box, Container, Paper, Typography } from "@mui/material";

import { MarkdownRenderer } from "../../components/MarkdownRenderer";
import { PRODUCT_NAME } from "../../utils/terminology";

const getAITransparencyContent = (brandName: string) => `## Understanding Our AI Technology

Spoke ${brandName} uses artificial intelligence to analyze voice and digital communications, providing automated transcription, sentiment analysis, conversation classification, and coaching insights. Our AI combines speech-to-text technology, natural language processing, and machine learning models trained specifically on business communication patterns.

## What Our AI Does Well

- **Transcription:** Accuracy as high as 93% on clear audio with standard business vocabulary
- **Pattern Recognition:** Identifies common conversation themes, compliance risks, and coaching opportunities
- **Trend Analysis:** Aggregates insights across conversations to identify systematic patterns
- **Real-time Classification:** Flags conversations requiring immediate attention or follow-up

## Known Limitations and Considerations

- **Accuracy Variability:** Performance decreases with poor audio quality, accents, technical jargon, or multiple speakers
- **Context Sensitivity:** May misinterpret sarcasm, implied meanings, or industry-specific terminology
- **Bias Potential:** Training data may reflect historical patterns that don't represent optimal outcomes
- **Probabilistic Nature:** AI outputs represent statistical predictions, not deterministic facts

## Your Responsibilities When Using AI Features

### Critical Decision Validation

Always verify AI outputs before taking action that affects:
- Employee performance evaluations or coaching decisions
- Compliance reporting or regulatory submissions
- Customer relationship management or escalations
- Business strategy or operational changes

### Professional Judgment Requirements

AI insights supplement but do not replace human expertise. Users must:
- Apply professional judgment when interpreting AI recommendations
- Consider context that AI may miss (customer history, market conditions, internal factors)
- Validate AI classifications against source recordings for important decisions
- Recognize when human review is necessary regardless of AI confidence levels

### Data Accuracy Verification

Before relying on AI-generated summaries, transcripts, or insights:
- Cross-reference key facts with original recordings
- Verify customer commitments, pricing discussions, or contractual terms
- Confirm compliance-related classifications with subject matter experts
- Document validation steps for audit purposes when required

## EnlightenMe: Exploratory Conversation Intelligence

EnlightenMe lets you ask open-ended questions about a filtered dataset of your conversation records, surfacing directional patterns and themes to guide your thinking. It is an exploratory tool. Use it to identify what is worth investigating further, not as a source of definitive answers.

The following limitations apply specifically to EnlightenMe responses:

- **Figures are estimates.** Numbers reflect pattern recognition across the dataset, not an exact count of individual records. Results may differ materially from a manual review.
- **Relationships between sub-groups are approximate.** Correlations between categories or nested subsets may not reflect the true distribution. Treat cross-group patterns as a starting point, not a confirmed finding.
- **Pattern reliability decreases with depth.** High-level themes are generally indicative. The further you drill into sub-groups of sub-groups, the greater the uncertainty.
- **Example calls are illustrative, not precise.** Where a specific call is cited to support an insight, it may not always be the exact source of the pattern identified. Verify cited calls before relying on them.
- **Correlation is not causation.** Associated patterns do not confirm cause and effect.
- **Sentiment and intent are interpretations.** Descriptions of customer emotion or behaviour are AI inferences, not verified facts.
- **Figures compound as analysis deepens.** Values derived from other estimates accumulate inaccuracy, so treat them as directional only.
- **Observation and inference are not always distinguished.** Not all statements carry the same evidential grounding. Apply your own judgement about which findings warrant closer scrutiny.
- **Summary-based analysis may lose detail.** Where summaries rather than full transcripts are used, information compression has already occurred before analysis begins.
- **Results are a snapshot, not a trend.** EnlightenMe returns a consolidated view across the selected period. Time-based shifts or operational changes within that window will not automatically surface.

Always validate significant findings against source data before making business decisions. For a full explanation of AI limitations in this product, refer to the sections below.

## Technical Transparency

### How Our AI Makes Decisions

Our conversation intelligence uses multiple AI models working together:
- **Speech Recognition:** Converts audio to text using acoustic and language models
- **Natural Language Processing:** Analyzes text for sentiment, intent, and key topics
- **Classification Models:** Categorizes conversations using trained business communication patterns
- **Anomaly Detection:** Flags unusual patterns that may require human attention

### Training Data and Bias Mitigation

- Models are trained on diverse business communication datasets
- Regular testing across demographic groups, industries, and communication styles
- Continuous monitoring for performance disparities or biased outputs
- Human oversight in model development and deployment decisions

## Quality Assurance and Continuous Improvement

### Model Updates and Changes

- AI models are updated regularly to improve accuracy and reduce bias
- Significant changes are communicated to administrators in advance
- Rollback procedures exist if updates negatively impact accuracy

### User Feedback Integration

Help us improve by reporting:
- Transcription errors or misclassifications
- Inappropriate AI recommendations or insights
- Bias or fairness concerns in AI outputs
- Missing context that led to incorrect analysis

## Implementation Best Practices

### Organizational Policies

Establish clear guidelines covering:
- When AI outputs require human validation
- Who has authority to act on AI recommendations
- Documentation requirements for AI-assisted decisions
- Escalation procedures when AI and human judgment conflict

### Training and Change Management

Ensure your team understands:
- AI capabilities and limitations specific to your use cases
- Appropriate validation procedures for different types of decisions
- Professional responsibility requirements in your industry
- How to report AI errors or concerns

### Technology Controls

Consider implementing:
- Required approval workflows for high-stakes AI recommendations
- Audit logging of AI usage and human validation steps
- Regular review of AI-assisted decisions and outcomes
- Technical controls preventing unvalidated AI outputs from reaching external parties

---

Document Version: 1.1

*This document supplements but does not replace your Master Services Agreement, Terms of Use, or other contractual obligations. For legal questions about AI usage in your specific context, consult with qualified counsel familiar with your industry's regulatory requirements.*`;

const AITransparency = () => {
  return (
    <Box sx={{ p: 3 }}>
      <Container maxWidth="md">
        <Paper sx={{ p: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Spoke {PRODUCT_NAME} AI Transparency and Responsible Use Guide
          </Typography>
          <Typography
            variant="subtitle1"
            color="text.secondary"
            sx={{ mb: 3, fontStyle: "italic" }}
          >
            Last updated: 26 March 2026
          </Typography>
          <MarkdownRenderer content={getAITransparencyContent(PRODUCT_NAME)} />
        </Paper>
      </Container>
    </Box>
  );
};

export default AITransparency;
